import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { Checkbox, Form, Input } from 'antd';
import { Button } from '@cropler/ui-kit';
import { Icon as CroplerIcon } from '@cropler/ui-kit';

import styles from './styles.module.scss';
import stylesModal from '../../styles/modal.module.scss';
import sensorImg from '../../assets/images/sensor.webp';
import sensorTurnOnImg from '../../assets/images/turn-on-sensor.webp';
import sensorConnectionImg from '../../assets/images/connection-sensor.webp';

import { sensorsApi, useCreateSensorMutation, useGetSensorByIdQuery } from '../../store/api/sensors';
import { DevicesQRCodeLinkKey } from './constants/index.jsx';
import { QRCodeDeviceId, Workspace } from '../../constants/keys';
import { ProgressSteps } from '../WorkspaceModal/ProgressSteps.jsx';
import { DevicesWorkspacesList } from './index.jsx';
import { setCurrentWorkspaceId } from '../../store/slices/workspaces';
import { setAvatarCompany, setCompanyName } from '../../store/slices/auth';
import { fieldsApi } from '../../store/api/fields';
import { statusWorkspaceModal } from '../../store/slices/ui';
import { GetDevicesAddFormSteps, GetDevicesAddFormStepsQRCode, GetFormatedSerialNumber10 } from './lib/index.jsx';
import { useViewport } from '../../hooks';
import { BreakpointMobileUp } from '../../constants/ui';
import { useGetWorkspacesQuery } from '../../store/api/workspaces';

const DevicesAddForm = ({ t, closePanel, clickNext }) => {
  const { widthViewport } = useViewport();

  const qRCodeDeviceId = localStorage.getItem(QRCodeDeviceId);
  const DevicesAddFormSteps = GetDevicesAddFormSteps(t);
  const DevicesAddFormStepsQRCode = GetDevicesAddFormStepsQRCode(t);
  const steps = qRCodeDeviceId ? DevicesAddFormStepsQRCode : DevicesAddFormSteps;

  // connecting params
  const intervalTime = 20000; // every 20 seconds
  const requestsNumber = 9; // 3 minutes -> 9 times

  const [form] = Form.useForm();
  const [formTurnOn] = Form.useForm();
  const dispatch = useDispatch();
  const [params, setSearchParams] = useSearchParams();
  const { currentWorkspaceId } = useSelector((state) => state.workspaces);
  const { currentUserWorkspaces } = useSelector((state) => state.auth);

  const [step, setStep] = useState(steps.add); // step
  const [isDeviceTurnedOn, setIsDeviceTurnedOn] = useState(false); // checkbox

  const [isConnecting, setIsConnecting] = useState(false);
  const [counter, setCounter] = useState(0);
  const [startDateTime, setStartDateTime] = useState(null);
  const intervalRef = useRef(null);
  const isDeviceConnected = useRef(false);
  const [workspaces, setWorkspaces] = useState([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState(currentWorkspaceId || '');
  const [formData, setFormData] = useState({ id: '', name: '' });

  const [
    createSensor,
    { isSuccess: isCreateSensorSuccess, error, isError: isCreateSensorError, data: createdSensor, reset },
  ] = useCreateSensorMutation();

  const { refetch } = useGetSensorByIdQuery(createdSensor ? { id: createdSensor.id } : undefined, {
    skip: !createdSensor || !createdSensor.id || !currentWorkspaceId,
    refetchOnMountOrArgChange: true,
  });

  const { isSuccess: isWorkspacesListSuccess, data: workspacesList = [] } = useGetWorkspacesQuery(
    { sensor__serial_number__nonexists: GetFormatedSerialNumber10(qRCodeDeviceId) },
    { skip: !qRCodeDeviceId, refetchOnMountOrArgChange: true },
  );

  // Create device
  const addDevice = ({ id, name }) => {
    const key = id && id.length < 12 ? id.replace(/-/g, '') : id; // short id or UUID
    createSensor({ serialNumber: key, name });
  };

  // (1) Add device (create sensor)
  const onAddDevice = () => {
    form
      .validateFields()
      .then((values) => {
        const valuesData = { id: values.deviceId, name: values.deviceName };
        if (qRCodeDeviceId) {
          // If QR code was scanned
          setStep(steps.workspaces);
          setFormData(valuesData);
        } else {
          addDevice(valuesData);
        }
      })
      .catch((errorInfo) => {
        console.log(errorInfo);
      });
  };

  // (2) Turn on sensor (checkbox) and start checking connection
  const onGoToConnection = () => {
    formTurnOn
      .validateFields()
      .then((values) => {
        if (values.turnOn) {
          onConnecting();
        }
      })
      .catch((errorInfo) => {
        console.log(errorInfo);
      });
  };

  // On connecting / Try connecting again
  const onConnecting = () => {
    setStep(steps.connecting);
    startCheckingConnect();
  };

  // (3) Start Interval for checking connection
  const startCheckingConnect = () => {
    setStartDateTime(new Date());
    setCounter(0);
    setIsConnecting(true);

    // If there was already an interval, clear it.
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // New interval
    intervalRef.current = setInterval(() => {
      setCounter((prev) => prev + 1);
    }, intervalTime);
  };

  // (3) Cancel checking connection and reset state
  const cancelCheckingConnect = () => {
    setIsConnecting(false);
    setCounter(0);

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  // If QR code was scanned
  const onChooseWorkspace = (id) => {
    setSelectedWorkspace(id);
  };

  // If QR code was scanned
  const onSaveWorkspace = () => {
    if (selectedWorkspace) {
      if (selectedWorkspace !== currentWorkspaceId) {
        // Change workspace
        const workspaceDataFilter = workspacesList.filter((item) => item.id === selectedWorkspace);
        if (!workspaceDataFilter) return;
        const workspaceData = workspaceDataFilter[0];
        localStorage.setItem(Workspace, JSON.stringify(selectedWorkspace));

        dispatch(setCurrentWorkspaceId({ workspaceId: selectedWorkspace }));
        dispatch(sensorsApi.util.resetApiState());
        dispatch(fieldsApi.util.resetApiState());
        setTimeout(() => {
          dispatch(setCompanyName({ userCompany: workspaceData.name }));
          dispatch(setAvatarCompany({ avatar: workspaceData.avatar }));
        }, 1);
      }

      // Create device from QR code
      if (formData && formData.id && formData.name) {
        setTimeout(() => {
          addDevice(formData);
        }, 2);
      }
    }
  };

  // If QR code was scanned
  const onClickAddWorkspace = () => {
    dispatch(statusWorkspaceModal(true));
    closePanel();
  };

  // Cancel and close modal
  const onCancel = () => {
    localStorage.removeItem(QRCodeDeviceId);
    cancelCheckingConnect();
    if (step.step > 0 && isCreateSensorSuccess) {
      dispatch(sensorsApi.util.resetApiState());

      // Open Create location modal
      setTimeout(() => {
        clickNext({ id: createdSensor.id, name: createdSensor.name });
      }, 100);
    }

    const nextParams = new URLSearchParams(params);
    nextParams.delete(DevicesQRCodeLinkKey);
    setSearchParams(nextParams, { replace: true });

    reset();
    closePanel();
    setStep(steps.add);
  };

  useEffect(() => {
    const serialNumber = params.get(DevicesQRCodeLinkKey);
    if (serialNumber) {
      localStorage.setItem(QRCodeDeviceId, serialNumber);
      form.setFieldValue('deviceId', serialNumber);
    } else {
      localStorage.removeItem(QRCodeDeviceId);
    }
  }, [params]);

  // (1) Create sensor success, next step 'Turn on'
  useEffect(() => {
    if (isCreateSensorSuccess && createdSensor.id) {
      setStep(steps.turnOn);
      // localStorage.removeItem(QRCodeDeviceId);
    }
  }, [isCreateSensorSuccess]);

  // Add sensor Error
  useEffect(() => {
    if (isCreateSensorError) {
      setStep(steps.add);
    }
  }, [isCreateSensorError, error]);

  // (3) Counter changed
  useEffect(() => {
    if (!isConnecting || counter === 0) return;
    if (counter > requestsNumber) {
      cancelCheckingConnect();
      if (!isDeviceConnected.current) {
        setStep(steps.error);
      }
      return;
    }

    const doCheck = async () => {
      const result = await refetch();

      const sync = result.data?.synced_at;
      if (sync) {
        const syncDate = new Date(sync);

        if (syncDate > startDateTime) {
          isDeviceConnected.current = true;
          cancelCheckingConnect();
          setStep(steps.success);
        }
      }
    };

    doCheck().then();
  }, [counter, isConnecting, refetch, startDateTime]);

  // Clear interval
  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  // Workspaces list
  useEffect(() => {
    if (!workspacesList || !isWorkspacesListSuccess) return;

    const roleByWorkspaceId = (currentUserWorkspaces || []).reduce((acc, item) => {
      const workspaceId = item?.workspace?.id;
      if (workspaceId) acc[workspaceId] = item.role;
      return acc;
    }, {});

    const workspacesListWithRole = workspacesList
      ? workspacesList.map((item) => {
          const workspaceId = item.id;
          const role = roleByWorkspaceId[workspaceId] || item.role;
          return { ...item, role };
        })
      : [];

    let list = [];

    workspacesListWithRole.map((item) => {
      if (item.role === 'owner') {
        list.push(item);
      }
    });
    if (list.length === 0) return;

    const sortedList = [...list].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));

    // Active workspace
    if (
      workspacesListWithRole.length &&
      currentWorkspaceId &&
      !workspacesListWithRole.find((item) => item.id === currentWorkspaceId)
    ) {
      setSelectedWorkspace(workspacesListWithRole[0].id);
    }

    setWorkspaces(sortedList);
  }, [workspacesList, isWorkspacesListSuccess, currentUserWorkspaces]);

  if (!step) return null;

  return (
    <div className={styles.deviceAdd}>
      <div className={styles.deviceAddBody}>
        <h2 className={`${styles.deviceAddTitle}${step.step === steps.error.step ? ' text-danger' : ''}`}>
          {step.title}
          {step.step === steps.connecting.step && (
            <span className={styles.deviceAddTitleDots}>
              <span>.</span>
              <span>.</span>
              <span>.</span>
            </span>
          )}
        </h2>

        {/* Progress (not error step) */}
        {step.step !== steps.error.step && (
          <div className={styles.deviceAddSteps}>
            <ProgressSteps step={step.stepLine} count={qRCodeDeviceId ? 3 : 2} />
          </div>
        )}

        <div className={styles.deviceAddForm}>
          {/* Add a device */}
          {step.step === steps.add.step && (
            <Form form={form} layout="vertical" requiredMark={false}>
              <Form.Item
                label={t('devices.name')}
                name="deviceName"
                rules={[
                  {
                    required: true,
                    type: 'string',
                    message: t('devices.namePlaceholder'),
                  },
                  {
                    type: 'string',
                    max: 64,
                    min: 1,
                    message: t('devices.nameParametersMessage'),
                  },
                ]}
              >
                <Input type="text" size="large" />
              </Form.Item>
              <Form.Item
                label={t('devices.id')}
                name="deviceId"
                rules={
                  qRCodeDeviceId
                    ? [
                        {
                          required: true,
                          type: 'string',
                          message: t('devices.idPlaceholder'),
                        },
                      ]
                    : [
                        {
                          required: true,
                          type: 'string',
                          message: t('devices.idPlaceholder'),
                        },
                        {
                          pattern: /^[a-zA-Z0-9-]{10,11}$/,
                          message: t('devices.idParametersMessage'),
                        },
                      ]
                }
              >
                <Input type="text" size="large" disabled={!!qRCodeDeviceId} rootClassName={'text-uppercase'} />
              </Form.Item>

              {isCreateSensorError && (
                <div className={styles.deviceAddFormError}>
                  <CroplerIcon name="info" />
                  {error[0].detail}
                </div>
              )}

              <div className={`${stylesModal.modalNav} ${styles.deviceAddFormNav}`}>
                <Button type="secondary" size={'large'} onClick={onCancel}>
                  {t('actions.cancel')}
                </Button>
                <Button type="primary" size={'large'} htmlType="submit" onClick={() => onAddDevice()}>
                  {t('actions.next')}
                  <CroplerIcon name="next" />
                </Button>
              </div>
            </Form>
          )}

          {/* Workspaces */}
          {qRCodeDeviceId && step.step === steps.workspaces.step && (
            <>
              <p>Please choose the workspace where the sensor should be added.</p>
              <DevicesWorkspacesList
                list={workspaces}
                clickWorkspace={onChooseWorkspace}
                clickAddWorkspace={onClickAddWorkspace}
                activeWorkspace={selectedWorkspace}
                t={t}
              />
              <div className={`${stylesModal.modalNav} ${styles.deviceAddFormNav}`}>
                <Button type="secondary" size={'large'} onClick={onCancel}>
                  {t('actions.cancel')}
                </Button>
                <Button type="primary" size={'large'} onClick={onSaveWorkspace}>
                  {t('actions.next')}
                </Button>
              </div>
            </>
          )}

          {/* Turn on device/Connecting */}
          {(step.step === steps.turnOn.step || step.step === steps.connecting.step) && (
            <>
              <p dangerouslySetInnerHTML={{ __html: t('devices.turtOnText') }}></p>
              {step.step === steps.turnOn.step && (
                <Form form={formTurnOn} layout="vertical" requiredMark={false}>
                  <Form.Item label="" name="turnOn" valuePropName="checked">
                    <Checkbox onChange={(e) => setIsDeviceTurnedOn(e.target.checked)}>
                      {t('devices.turtOnCheckbox')}
                    </Checkbox>
                  </Form.Item>

                  <div className={`${stylesModal.modalNav} ${styles.deviceAddFormNav}`}>
                    <Button type="secondary" size={'large'} onClick={onCancel}>
                      {t('actions.cancel')}
                    </Button>
                    <Button
                      type="primary"
                      disabled={!isDeviceTurnedOn}
                      size={'large'}
                      htmlType="submit"
                      onClick={() => onGoToConnection()}
                    >
                      {t('actions.next')}
                      <CroplerIcon name="next" />
                    </Button>
                  </div>
                </Form>
              )}

              {step.step === steps.connecting.step && (
                <>
                  <p dangerouslySetInnerHTML={{ __html: t('devices.turtOnText2') }}></p>
                  <div className={`${stylesModal.modalNav} ${styles.deviceAddFormNav}`}>
                    <Button type="secondary" size={'large'} onClick={onCancel}>
                      {t('actions.cancel')}
                    </Button>
                  </div>
                </>
              )}
            </>
          )}

          {/* Error */}
          {step.step === steps.error.step && (
            <>
              <p dangerouslySetInnerHTML={{ __html: t('devices.error') }}></p>
              <div className={`${stylesModal.modalNav} ${styles.deviceAddFormNav}`}>
                <Button type="secondary" size={'large'} onClick={onCancel}>
                  {t('actions.cancel')}
                </Button>
                <Button type="primary" size={'large'} onClick={onConnecting}>
                  {t('actions.tryAgain')}
                </Button>
              </div>
            </>
          )}

          {/* Sensor connected */}
          {step.step === steps.success.step && (
            <>
              <p dangerouslySetInnerHTML={{ __html: t('devices.errorConnect') }}></p>
              <div className={`${stylesModal.modalNav} ${styles.deviceAddFormNav}`}>
                <Button type="primary" size={'large'} onClick={onCancel}>
                  {t('actions.finish')}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Image */}
      {!(qRCodeDeviceId && step.step === steps.workspaces.step && widthViewport < BreakpointMobileUp) && (
        <div
          className={`${styles.deviceAddImage}${step.step > steps.turnOn.step ? ' ' + styles.deviceAddImageNoBorder : ''}`}
        >
          {step.step === steps.add.step && <img src={sensorImg || ''} alt="Cropler Device" width={320} />}
          {step.step === steps.turnOn.step && <img src={sensorTurnOnImg || ''} alt="Cropler Device" width={320} />}
          {step.step > steps.turnOn.step && <img src={sensorConnectionImg || ''} alt="Cropler Device" width={320} />}
        </div>
      )}
    </div>
  );
};

export default DevicesAddForm;
